import { useEffect, useRef } from "react";
import { cn } from "../lib/cn";

export interface Chip {
  id: string;
  label: string;
  /** A small secondary figure, e.g. how many lines a player has. */
  hint?: string;
  /** Marked with a dot — the main line. */
  marked?: boolean;
}

/**
 * One labelled row of choices — markets, players, lines. Scrolls sideways rather than wrapping,
 * so a game with 19 players is one tidy row, and keeps the current choice in view.
 */
export function ChipRow({
  label,
  chips,
  value,
  onChange,
  action,
  search,
}: {
  label: string;
  chips: Chip[];
  value: string | null;
  onChange: (id: string) => void;
  /** A text action at the right of the label, e.g. "Back to main line". */
  action?: { label: string; onClick: () => void } | null;
  /** A filter box beside the label, for long rows. */
  search?: { value: string; onChange: (v: string) => void; placeholder: string } | null;
}) {
  const current = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    current.current?.scrollIntoView({ block: "nearest", inline: "center" });
  }, [value]);

  return (
    <div className="min-w-0">
      <div className="mb-2 flex items-center justify-between gap-3 px-1">
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-faint">{label}</span>
        <span className="flex items-center gap-3">
          {search && (
            <input
              type="search"
              value={search.value}
              onChange={(e) => search.onChange(e.target.value)}
              placeholder={search.placeholder}
              className="w-40 rounded-full border border-line bg-panel px-3 py-1 text-[12px] text-ink outline-none placeholder:text-faint focus:border-accent"
            />
          )}
          {action && (
            <button type="button" onClick={action.onClick} className="text-[12px] font-semibold text-accent hover:underline">
              {action.label}
            </button>
          )}
        </span>
      </div>
      <div className="no-scrollbar -mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1">
        {chips.map((chip) => {
          const active = chip.id === value;
          return (
            <button
              key={chip.id}
              ref={active ? current : undefined}
              type="button"
              onClick={() => onChange(chip.id)}
              aria-pressed={active}
              className={cn(
                "relative flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition-colors",
                active ? "brand-gradient text-accent-ink" : "bg-cell text-dim hover:bg-cell-hi hover:text-ink",
              )}
            >
              {chip.label}
              {chip.hint && <span className={cn("text-[11px]", active ? "opacity-70" : "text-faint")}>{chip.hint}</span>}
              {chip.marked && !active && (
                <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-accent" title="Main line" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
